import React, { useMemo, useRef, useState } from 'react';
import { IconedBox } from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/IconecBox';
import { WidgetTextFieldV2 } from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/WidgetTextFieldV2';
import { Autocomplete, Box, Typography } from '@mui/material';
import LocationIcon from 'views/common/components/UI/Icons/LocationIcon';
import { ERRORS_PREFIX } from 'lib/utils/mainWidgetConstants';
import StyledBox, {
  StyledPopper,
} from 'views/rebranded-module/common/components/Logical/MainWidgetV2/components/WidgetLocationFieldV2/WidgetLocationFieldV2.styles';
import ArrowIcon from 'views/common/components/UI/Icons/ArrowIcon';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const WidgetLocationFieldV2 = (props) => {
  const {
    selectedLocationId,
    fieldName,
    locationsList = [],
    label,
    placeholder,
    handleInputChange,
    handleLocationChange,
    handleRenderAllWidget,
    loading,
    errors,
    disabled,
    className,
    fieldType,
    dataTestIdPrefix,
  } = props;

  const content = useContent('RentalWidgetComponent');

  const [isFocused, setIsFocused] = useState('');
  const autoCompleteInputRef = useRef();

  const selectedLocation = useMemo(
    () => locationsList.find(
      (location) => location.id && location.id === selectedLocationId,
    ) || null,
    [selectedLocationId, locationsList],
  );

  const placeHolder = useMemo(() => {
    if (isFocused && selectedLocation) {
      return `${selectedLocation.display_name}, ${selectedLocation.region_name}`;
    }
    return placeholder;
  }, [selectedLocation, isFocused, placeholder]);

  const fieldValue = useMemo(() => {
    if (isFocused) return null;
    return selectedLocation;
  }, [isFocused, selectedLocation]);

  return (
    <StyledBox className={className}>
      <IconedBox
        error={!!errors[`${ERRORS_PREFIX}${fieldName}`]}
        icon={<LocationIcon />}>
        <Autocomplete
          PopperComponent={StyledPopper}
          value={fieldValue}
          options={loading ? [] : locationsList}
          filterOptions={(x) => x}
          getOptionLabel={(option) => `${option.display_name}, ${option.region_name}`}
          id={`${ERRORS_PREFIX}${fieldName}`}
          error={!!errors[`${ERRORS_PREFIX}${fieldName}`]}
          //  helperText={errors[`${ERRORS_PREFIX}${fieldName}`] || ''}
          className="WidgetInput"
          autoComplete
          disableClearable
          includeInputInLis
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          popupIcon={<ArrowIcon />}
          onInputChange={(e, value, reason) => {
            if (reason !== 'reset') {
              handleInputChange(fieldType, value);
            }
          }}
          disabled={disabled}
          onChange={(e, value) => {
            setIsFocused(true);
            handleLocationChange(fieldName, value?.id);
            autoCompleteInputRef.current.blur();
          }}
          onOpen={() => handleRenderAllWidget?.(true)}
          loading={loading}
          renderOption={(params, option) => {
            const optionLabel = `${option.display_name}, ${option.region_name}`;
            const encoded = encodeURIComponent(optionLabel);
            return (
              <li
                {...params}
                data-testid={
                  dataTestIdPrefix
                    ? `${dataTestIdPrefix}-option-${encoded}`
                    : undefined
                }>
                <Box>
                  <Typography
                    className="WLFOptionText"
                    dangerouslySetInnerHTML={{
                      __html: `${option.display_name}, <b>${option.region_name}</b>`,
                    }}
                  />
                  {option.distance >= 0 && (
                    <Typography>
                      {`${option.distance} ${content.milesAwayText} ${option.formattedAddress}`}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <WidgetTextFieldV2
              {...params}
              inputProps={{
                ...params.inputProps,
                ...(dataTestIdPrefix && {
                  'data-testid': `${dataTestIdPrefix}-input`,
                }),
              }}
              inputRef={autoCompleteInputRef}
              placeholder={placeHolder}
              label={label}
              isFocused={isFocused}
              variant="filled"
              error={!!errors[`${ERRORS_PREFIX}${fieldName}`]}
            />
          )}
        />
      </IconedBox>
    </StyledBox>
  );
};

export { WidgetLocationFieldV2 };
